import { FC } from 'react';

import { IRoutingConfigVersion } from '@entities/routing/config-version';
import ModalConfirm from '@features/modal-confirm';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PublishedWithChangesOutlinedIcon from '@mui/icons-material/PublishedWithChangesOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import { Tooltip } from '@mui/material';
import { useModal } from '@shared/components/Modal';

import PublishModal from './PublishModal';

interface IProps {
  version: IRoutingConfigVersion;
  isEditing: boolean;
  // A Restore request is in flight: the bar waits for the draft before offering more.
  isRestoring: boolean;
  onEdit: () => void;
  onRestore: () => void;
}

// The two rollback paths read differently on purpose: Activate goes live at once,
// Restore only opens editing. MUI tooltips, not native titles: they open at once
// instead of after the browser's second-long hover delay.
const ACTIVATE_HINT =
  'Publish this archived version again, as-is. It becomes the active config right away — no draft in between.';
const RESTORE_HINT =
  'Create a new draft from this version and open it for editing. Nothing goes live until you publish the draft.';

// Bar actions. Edit Config on any status; Publish on a saved draft; Activate and
// Restore on an archived version. The modals own the confirmation, the comment and
// the request.
const ConfigActions: FC<IProps> = ({ version, isEditing, isRestoring, onEdit, onRestore }) => {
  const { openModal, closeModal } = useModal();

  // The edit-session banner carries the controls while editing.
  if (isEditing) return null;

  const openPublish = () => openModal(<PublishModal version={version} />, 'md');

  // Restore creates a draft on the server, so a stray click gets a second look.
  const confirmRestore = () =>
    openModal(
      <ModalConfirm
        title={`Restore v${version.id} as a new draft?`}
        message={`A draft copy of v${version.id} “${version.name}” is created and opened for editing. v${version.id} itself stays archived, and nothing goes live until you publish the draft.`}
        onConfirm={() => {
          closeModal();
          onRestore();
        }}
        onCancel={closeModal}
      />,
      'sm',
    );

  return (
    <>
      {version.status === 'draft' && (
        <button
          type='button'
          className='success-btn routing-config-action-btn'
          onClick={openPublish}
        >
          <PublishOutlinedIcon fontSize='small' /> Publish
        </button>
      )}
      {version.status === 'archived' && (
        <>
          {/* The spans anchor the tooltips: button:disabled has pointer-events: none */}
          <Tooltip title={ACTIVATE_HINT}>
            <span className='routing-config-action-wrap'>
              <button
                type='button'
                className='success-btn routing-config-action-btn'
                onClick={openPublish}
                disabled={isRestoring}
              >
                <PublishedWithChangesOutlinedIcon fontSize='small' /> Activate
              </button>
            </span>
          </Tooltip>
          <Tooltip title={RESTORE_HINT}>
            <span className='routing-config-action-wrap'>
              <button
                type='button'
                className='routing-version-switcher-btn routing-config-action-btn'
                onClick={confirmRestore}
                disabled={isRestoring}
              >
                <RestoreOutlinedIcon fontSize='small' /> {isRestoring ? 'Restoring…' : 'Restore'}
              </button>
            </span>
          </Tooltip>
        </>
      )}
      <button
        type='button'
        className='routing-config-action-btn'
        onClick={onEdit}
        disabled={isRestoring}
      >
        <EditOutlinedIcon fontSize='small' /> Edit Config
      </button>
    </>
  );
};

export default ConfigActions;
